package horcrux.frontend.common.views.documents

import horcrux.common.api.dtos.types.DocumentID
import horcrux.common.api.dtos.types.DocumentMetadata
import horcrux.frontend.common.BooleanState
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.State
import horcrux.frontend.common.configs.StaticContentConfigs
import horcrux.frontend.common.views.components.input.selectors.MenuOptionEnum
import horcrux.frontend.common.views.components.input.selectors.MenuOptionEnumCompanion
import horcrux.frontend.common.views.components.input.selectors.MenuSelectorProps
import horcrux.frontend.common.views.components.input.selectors.menuSelector
import horcrux.frontend.common.views.documents.dialogs.DeleteDocumentDialog
import horcrux.frontend.common.views.documents.dialogs.DocumentDetailsDialog
import horcrux.frontend.common.views.documents.dialogs.EditDocumentNameDialog
import js.objects.jso
import mui.material.SvgIconProps
import mui.material.SvgIconSize
import mui.system.sx
import react.FC
import react.Props

enum class DocumentNavigatorMenuOption(override val viewName: String) : MenuOptionEnum {
    VIEW_DOCUMENT_DETAILS("View Document Details"),
    EDIT_DOCUMENT_NAME("Edit Document Name"),
    DELETE_DOCUMENT("Delete Document");

    override val alwaysVisible: Boolean = true

    companion object : MenuOptionEnumCompanion<DocumentNavigatorMenuOption> {
        override val menuOptions: List<DocumentNavigatorMenuOption> = DocumentNavigatorMenuOption.entries.toList()
        override val menuView: FC<MenuSelectorProps<DocumentNavigatorMenuOption>> =
            menuSelector("document-navigator-menu")
    }
}

enum class ChatPageDocumentNavigatorMenuOption(override val viewName: String) : MenuOptionEnum {
    VIEW_DOCUMENT_DETAILS("View Document Details");

    override val alwaysVisible: Boolean = true

    companion object : MenuOptionEnumCompanion<ChatPageDocumentNavigatorMenuOption> {
        override val menuOptions: List<ChatPageDocumentNavigatorMenuOption> =
            ChatPageDocumentNavigatorMenuOption.entries.toList()
        override val menuView: FC<MenuSelectorProps<ChatPageDocumentNavigatorMenuOption>> =
            menuSelector("chat-page-document-navigator-menu")
    }
}

private fun List<DocumentMetadata>.removeDocument(documentID: DocumentID): List<DocumentMetadata> =
    this.filterNot { it.documentID == documentID }

external interface EditDocumentMenuProps : Props {
    var documentMetadata: DocumentMetadata
    var documentMetadataListState: State<List<DocumentMetadata>>
    var globalAppState: GlobalAppState
}

val EditDocumentMenu = FC<EditDocumentMenuProps> { props ->
    val showDocumentDetailsDialogState = BooleanState()
    val showRenameDocumentDialogState = BooleanState()
    val showDeleteDocumentDialogState = BooleanState()
    val documentMetadataList = props.documentMetadataListState.get()

    DocumentDetailsDialog {
        this.showDialogState = showDocumentDetailsDialogState
        this.documentMetadata = props.documentMetadata
    }

    EditDocumentNameDialog {
        this.showDialogState = showRenameDocumentDialogState
        this.currentDocumentMetadata = props.documentMetadata
        this.documentMetadataList = documentMetadataList
        this.onSuccess = { documentMetadata ->
            val newDocumentMetadataList = documentMetadataList
                .removeDocument(currentDocumentMetadata.documentID) + documentMetadata
            props.documentMetadataListState.set(newDocumentMetadataList)
        }
        this.globalAppState = props.globalAppState
    }

    DeleteDocumentDialog {
        this.showDialogState = showDeleteDocumentDialogState
        this.currentDocumentMetadata = props.documentMetadata
        this.onSuccess = {
            val newDocumentMetadataList =
                documentMetadataList.removeDocument(currentDocumentMetadata.documentID)
            props.documentMetadataListState.set(newDocumentMetadataList)
        }
        this.globalAppState = props.globalAppState
    }

    val svgIconProps = jso<SvgIconProps> {
        this.fontSize = SvgIconSize.medium
        this.sx {
            this.color = StaticContentConfigs.BLUE
        }
    }
    props
        .globalAppState
        .chatPageDataMaybeState
        .onSome {
            ChatPageDocumentNavigatorMenuOption.menuView {
                this.svgIconPropsMaybe = svgIconProps
                this.onClick = { menuOption ->
                    when (menuOption) {
                        ChatPageDocumentNavigatorMenuOption.VIEW_DOCUMENT_DETAILS -> showDocumentDetailsDialogState.setTrue()
                    }
                }
            }
        }
        .onNone {
            DocumentNavigatorMenuOption.menuView {
                this.svgIconPropsMaybe = svgIconProps
                this.onClick = { menuOption ->
                    when (menuOption) {
                        DocumentNavigatorMenuOption.VIEW_DOCUMENT_DETAILS -> showDocumentDetailsDialogState.setTrue()
                        DocumentNavigatorMenuOption.EDIT_DOCUMENT_NAME -> showRenameDocumentDialogState.setTrue()
                        DocumentNavigatorMenuOption.DELETE_DOCUMENT -> showDeleteDocumentDialogState.setTrue()
                    }
                }
            }
        }
}
